/**
 * FSRS (Free Spaced Repetition Scheduler) Service
 *
 * Wraps the ts-fsrs package to provide flashcard scheduling functionality.
 * FSRS is more efficient than traditional SM-2 algorithm, with better retention
 * and optimal review intervals based on memory research.
 *
 * Key features:
 * - 4 rating buttons: Again (1), Hard (2), Good (3), Easy (4)
 * - Dynamic difficulty adjustment
 * - Memory stability tracking
 * - Optimal scheduling intervals
 *
 * Based on: https://github.com/open-spaced-repetition/ts-fsrs
 */

import {
  fsrs,
  generatorParameters,
  createEmptyCard,
  Rating,
  State,
  type Card,
  type ReviewLog,
  type FSRSParameters,
  type RecordLog,
} from 'ts-fsrs';
import type { Flashcard } from '../types/flashcard';
import { supabase } from './supabase';

/**
 * FSRS Configuration
 * These parameters can be customized based on user preferences
 */
export const DEFAULT_FSRS_PARAMS: Partial<FSRSParameters> = {
  // Maximum interval between reviews (in days)
  maximum_interval: 365,

  // Enable fuzzing to add randomness to intervals (prevents card bunching)
  enable_fuzz: true,

  // Enable short-term memory optimization
  enable_short_term: true,

  // Request retention rate (0.9 = 90% retention target)
  request_retention: 0.9,
};

/**
 * Initialize FSRS scheduler with custom parameters
 */
export function createScheduler(customParams?: Partial<FSRSParameters>) {
  const params = generatorParameters({ ...DEFAULT_FSRS_PARAMS, ...customParams });
  return fsrs(params);
}

/**
 * Default FSRS scheduler instance
 */
export const scheduler = createScheduler();

/**
 * Rating labels for UI display
 */
export const RATING_LABELS = {
  [Rating.Again]: { label: 'Znovu', color: 'destructive', description: 'Nevzpomněl/a jsem si' },
  [Rating.Hard]: { label: 'Těžké', color: 'warning', description: 'Vzpomněl/a jsem si s obtížemi' },
  [Rating.Good]: { label: 'Dobré', color: 'primary', description: 'Vzpomněl/a jsem si správně' },
  [Rating.Easy]: { label: 'Snadné', color: 'success', description: 'Vzpomněl/a jsem si snadno' },
} as const;

/**
 * State labels for UI display
 */
export const STATE_LABELS = {
  [State.New]: 'Nová',
  [State.Learning]: 'Učím se',
  [State.Review]: 'Opakování',
  [State.Relearning]: 'Přeučení',
} as const;

/**
 * Convert Flashcard to FSRS Card format
 */
export function flashcardToFSRSCard(flashcard: Flashcard): Card {
  // If card has FSRS data, use it
  if (flashcard.due && flashcard.state) {
    return {
      due: new Date(flashcard.due),
      stability: flashcard.stability || 0,
      difficulty: flashcard.difficulty || 0,
      elapsed_days: flashcard.elapsed_days || 0,
      scheduled_days: flashcard.scheduled_days || 0,
      reps: flashcard.reps || 0,
      lapses: flashcard.lapses || 0,
      state: flashcard.state as State,
      last_review: flashcard.last_review ? new Date(flashcard.last_review) : undefined,
    };
  }

  // Otherwise create a new card
  return createEmptyCard();
}

/**
 * Get scheduling cards for all rating options
 * Returns the card state after each possible rating
 */
export function getSchedulingOptions(flashcard: Flashcard, now: Date = new Date()) {
  const card = flashcardToFSRSCard(flashcard);
  const schedulingCards = scheduler.repeat(card, now);

  return {
    [Rating.Again]: schedulingCards[Rating.Again],
    [Rating.Hard]: schedulingCards[Rating.Hard],
    [Rating.Good]: schedulingCards[Rating.Good],
    [Rating.Easy]: schedulingCards[Rating.Easy],
  };
}

/**
 * Schedule card based on user rating
 * Returns updated card and review log
 */
export function scheduleCard(
  flashcard: Flashcard,
  rating: Rating,
  now: Date = new Date()
): RecordLog {
  const card = flashcardToFSRSCard(flashcard);
  return scheduler.next(card, now, rating);
}

/**
 * Save review to database
 * Updates flashcard FSRS fields and creates review log entry
 */
export async function saveReview(
  flashcardId: string,
  rating: Rating,
  now: Date = new Date()
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get current flashcard data
    const { data: flashcard, error: fetchError } = await supabase
      .from('flashcards')
      .select('*')
      .eq('id', flashcardId)
      .single();

    if (fetchError || !flashcard) {
      return { success: false, error: 'Flashcard not found' };
    }

    // Convert to Flashcard type (with FSRS fields)
    const flashcardData: Flashcard = {
      id: flashcard.id,
      primary: flashcard.primary_text,
      target: flashcard.target_text,
      phonetic: flashcard.phonetic || '',
      audioUrl: flashcard.audio_url || undefined,
      isLearned: flashcard.is_learned || false,
      targetLanguage: { id: '', name: '', code: '' }, // Will be populated by join
      examples: flashcard.examples as string[] | undefined,
      createdAt: new Date(flashcard.created_at),
      updatedAt: new Date(flashcard.updated_at),
      // FSRS fields
      due: flashcard.due || undefined,
      stability: flashcard.stability || undefined,
      difficulty: flashcard.difficulty || undefined,
      elapsed_days: flashcard.elapsed_days || undefined,
      scheduled_days: flashcard.scheduled_days || undefined,
      reps: flashcard.reps || undefined,
      lapses: flashcard.lapses || undefined,
      state: flashcard.state || undefined,
      last_review: flashcard.last_review || undefined,
    };

    // Schedule the card
    const result = scheduleCard(flashcardData, rating, now);
    const newCard = result.card;
    const log = result.log;

    // Update flashcard with new FSRS data
    const { error: updateError } = await supabase
      .from('flashcards')
      .update({
        due: newCard.due.toISOString(),
        stability: newCard.stability,
        difficulty: newCard.difficulty,
        elapsed_days: newCard.elapsed_days,
        scheduled_days: newCard.scheduled_days,
        reps: newCard.reps,
        lapses: newCard.lapses,
        state: newCard.state,
        last_review: now.toISOString(),
        updated_at: now.toISOString(),
      })
      .eq('id', flashcardId);

    if (updateError) {
      console.error('Error updating flashcard:', updateError);
      return { success: false, error: updateError.message };
    }

    // Save review log
    const { error: logError } = await supabase.from('review_logs').insert({
      flashcard_id: flashcardId,
      user_id: flashcard.user_id,
      rating: rating,
      state: log.state,
      due: log.due.toISOString(),
      stability: log.stability,
      difficulty: log.difficulty,
      elapsed_days: log.elapsed_days,
      last_elapsed_days: log.last_elapsed_days,
      scheduled_days: log.scheduled_days,
      review_time: now.toISOString(),
    });

    if (logError) {
      console.error('Error saving review log:', logError);
      // Don't fail the review if log save fails
    }

    return { success: true };
  } catch (error) {
    console.error('Error in saveReview:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get cards due for review
 * Returns flashcards that should be reviewed now or are overdue
 */
export async function getDueCards(userId: string, limit: number = 20) {
  const now = new Date();

  const { data, error } = await supabase
    .from('flashcards')
    .select('*, flashcard_target_languages(*), flashcard_categories(*)')
    .eq('user_id', userId)
    .lte('due', now.toISOString())
    .order('due', { ascending: true })
    .limit(limit);

  if (error) {
    console.error('Error fetching due cards:', error);
    return [];
  }

  return data || [];
}

/**
 * Get new cards (cards that haven't been studied yet)
 */
export async function getNewCards(userId: string, limit: number = 20) {
  const { data, error } = await supabase
    .from('flashcards')
    .select('*, flashcard_target_languages(*), flashcard_categories(*)')
    .eq('user_id', userId)
    .eq('state', State.New)
    .order('created_at', { ascending: true })
    .limit(limit);

  if (error) {
    console.error('Error fetching new cards:', error);
    return [];
  }

  return data || [];
}

/**
 * Get review statistics for a user
 */
export async function getReviewStats(userId: string, days: number = 30) {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  const { data, error } = await supabase
    .from('review_logs')
    .select('*')
    .eq('user_id', userId)
    .gte('review_time', startDate.toISOString());

  if (error) {
    console.error('Error fetching review stats:', error);
    return null;
  }

  const totalReviews = data.length;
  const ratingCounts = {
    again: data.filter((r) => r.rating === Rating.Again).length,
    hard: data.filter((r) => r.rating === Rating.Hard).length,
    good: data.filter((r) => r.rating === Rating.Good).length,
    easy: data.filter((r) => r.rating === Rating.Easy).length,
  };

  // Calculate retention rate (Good + Easy / Total)
  const retention =
    totalReviews > 0
      ? ((ratingCounts.good + ratingCounts.easy) / totalReviews) * 100
      : 0;

  return {
    totalReviews,
    ratingCounts,
    retention: Math.round(retention),
    days,
  };
}

/**
 * Format interval for display
 */
export function formatInterval(days: number): string {
  if (days < 1) {
    const minutes = Math.round(days * 24 * 60);
    return `${minutes} min`;
  } else if (days === 1) {
    return '1 den';
  } else if (days < 7) {
    return `${Math.round(days)} dny`;
  } else if (days < 30) {
    const weeks = Math.round(days / 7);
    return `${weeks} ${weeks === 1 ? 'týden' : 'týdny'}`;
  } else if (days < 365) {
    const months = Math.round(days / 30);
    return `${months} ${months === 1 ? 'měsíc' : 'měsíce'}`;
  } else {
    const years = Math.round(days / 365);
    return `${years} ${years === 1 ? 'rok' : 'roky'}`;
  }
}

/**
 * Check if a card is due for review
 */
export function isCardDue(flashcard: Flashcard, now: Date = new Date()): boolean {
  if (!flashcard.due) return true; // New cards are always due
  return new Date(flashcard.due) <= now;
}

/**
 * Get next review date for a card
 */
export function getNextReviewDate(flashcard: Flashcard): Date | null {
  if (!flashcard.due) return null;
  return new Date(flashcard.due);
}
